/**
 * Snapshot utility for capturing camera and screen images.
 */
class Snapshot {
    constructor() {
        // Source Inputs
        this.camera = null; // The HTMLVideoElement for the webcam
        this.screen = null; // The MediaStream for the screen share

        // Internal Processing
        this.canvas = document.createElement("canvas");
        this.ctx = this.canvas.getContext("2d");

        this.config = {
            image_quality: 0.8,
            image_format: "image/jpeg",
        };


        this.intervalId = null;

        this.callbacks = {
            on_capture: null,
            on_error: null,
        };
    }

    /**
     * Initialize the utility with camera and screen sources.
     */
    init(options = {}) {
        // Handle both camelCase and snake_case for the transition
        const on_capture = options.on_capture || options.onCapture;
        const on_error = options.on_error || options.onError;

        // Merge config overrides
        if (options.image_quality !== undefined)
            this.config.image_quality = options.image_quality;
        if (options.image_format !== undefined)
            this.config.image_format = options.image_format;

        // Support camelCase overrides if they exist
        if (options.imageQuality !== undefined)
            this.config.image_quality = options.imageQuality;
        if (options.imageFormat !== undefined)
            this.config.image_format = options.imageFormat;

        // Map simplified names
        this.camera = options.camera || this.camera;
        this.screen = options.screen || this.screen;

        if (options.periodic_interval !== undefined)
            this.config.periodic_interval = options.periodic_interval;

        if (on_capture) this.callbacks.on_capture = on_capture;
        if (on_error) this.callbacks.on_error = on_error;

        this.startPeriodic();
    }

    startPeriodic() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }

        if (this.config.periodic_interval > 0) {
            // console.log(`[Snapshot] Starting periodic capture every ${this.config.periodic_interval}ms`);
            this.intervalId = setInterval(async () => {
                await this.capture(
                    ["camera", "screen"],
                    "periodic_snapshot",
                );
            }, this.config.periodic_interval);
        }
    }

    stop() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }

    /**
     * Captures snapshots from specified sources.
     * @param {string[]} types - e.g., ['camera', 'screen']
     * @param {string} event_name - name of the trigger event
     */
    async capture(types = ["camera", "screen"], event_name = "manual_capture") {
        const results = { camera: null, screen: null };

        const capture_task = async (type) => {
            let source = null;
            let is_temp = false;

            try {
                if (type === "camera") {
                    if (!this.camera || this.camera.readyState < 2) {
                        throw new Error("Camera not ready");
                    }
                    source = this.camera;
                } else if (type === "screen") {
                    if (!this.screen || !this.screen.active) {
                        throw new Error("Screen stream not active");
                    }
                    // We need a temp video element to "see" the screen stream
                    source = document.createElement("video");
                    source.srcObject = this.screen;
                    source.muted = true;
                    is_temp = true;
                    await source.play();
                }

                if (!source) return;

                const { videoWidth: w, videoHeight: h } = source;
                if (w === 0 || h === 0)
                    throw new Error(`Invalid dimensions for ${type}`);

                this.canvas.width = w;
                this.canvas.height = h;
                this.ctx.drawImage(source, 0, 0, w, h);

                const blob = await new Promise((resolve) =>
                    this.canvas.toBlob(
                        resolve,
                        this.config.image_format,
                        this.config.image_quality,
                    ),
                );

                if (blob) {
                    results[type] = blob;
                }
            } catch (err) {
                console.warn(`[Snapshot] ${type} failed:`, err.message);
                this.callbacks.on_error?.(err, type, event_name);
            } finally {
                if (is_temp && source) {
                    source.pause();
                    source.srcObject = null;
                    source.remove();
                }
            }
        };

        await Promise.all(types.map((type) => capture_task(type)));

        // Fire callback once with all results
        this.callbacks.on_capture?.(results, event_name);

        return results;
    }
}

export default new Snapshot();
